import { type ComponentType, type FC, type ReactNode } from "react";

import { type FormFieldProps } from "@snapchat/snap-design-system";
import { type FieldProps } from "formik";

/**
 * @param WrappedComponent the sds component which conforms to the FormFieldProps interface
 * @returns a React Component which can be supplied to formik's Field component
 *
 * @example
 * import { Input } from '@snapchat/snap-design-system'
 * import { Field } from 'formik'
 *
 * // create the wrapper component
 * const TextFormField = formikAdapter(Input)
 * // render the wrapper component into formik
 * <Field name="" component={TextFormField} />
 */
export const formikAdapter = <T,>(
    WrappedComponent: ComponentType<{ children?: ReactNode } & FormFieldProps<T>>,
): FC<{ children?: ReactNode } & FieldProps<T>> => {
    return ({ field, form, children, ...rest }) => (
        <WrappedComponent
            {...rest}
            onChange={(value) => {
                void form.setFieldValue(field.name, value);
            }}
            onBlur={() => {
                void form.setFieldTouched(field.name, true);
            }}
            value={field.value}
        >
            {children}
        </WrappedComponent>
    );
};
